import React, { useState, useEffect } from "react";
import { ModalCloseButton, Text, Spinner } from "@chakra-ui/react";
import { ModalNew as Modal } from "components/Modal/ModalNew";
import { SearchBox } from "components/SearchBox";
import { useQuery } from "@tanstack/react-query";
import { searchKeyPersonnelProjects } from "actions/searchActions";
import { KeyPersonnelProject } from "types/key-personnel.types";
import { PersonnelProjectCard } from "./PersonnelProjectCard";

type PersonnelProjectsModalProps = {
	isOpen: boolean;
	onClose: () => void;
	projects: KeyPersonnelProject[];
	personnelName: string;
	personnelId: number;
};

export const PersonnelProjectsModal = ({
	isOpen,
	onClose,
	projects,
	personnelName,
	personnelId,
}: PersonnelProjectsModalProps) => {
	const [searchParams, setSearchParams] = useState<{
		search: string;
		sectors: string[];
	} | null>(null);
	const [displayedProjects, setDisplayedProjects] =
		useState<KeyPersonnelProject[]>(projects);

	// Query for personnel projects using object-based syntax for newer React Query versions
	const { isLoading, error } = useQuery({
		queryKey: ["personnelProjects", personnelId, searchParams],
		queryFn: () =>
			searchKeyPersonnelProjects(
				personnelId,
				searchParams || { search: "", sectors: [] }
			),
		enabled: isOpen && searchParams !== null,
	});

	// Set up effect to update displayed projects when the query succeeds
	useEffect(() => {
		async function fetchProjects() {
			if (isOpen && searchParams !== null) {
				try {
					const result = await searchKeyPersonnelProjects(
						personnelId,
						searchParams
					);
					if (result.status) {
						setDisplayedProjects(result.data);
					}
				} catch (err) {
					console.error("Error fetching projects:", err);
				}
			}
		}

		fetchProjects();
	}, [isOpen, personnelId, searchParams]);

	// Reset displayed projects when modal is opened/closed or projects change
	useEffect(() => {
		if (isOpen) {
			setDisplayedProjects(projects);
		}
	}, [isOpen, projects]);

	const onFilterSubmit = (values: {
		keyword?: string;
		sector?: string | string[];
	}) => {
		const sectors = Array.isArray(values.sector)
			? values.sector
			: values.sector
				? [values.sector]
				: [];

		setSearchParams({
			search: values.keyword || "",
			sectors: sectors,
		});
	};

	if (!isOpen) return null;

	const headerContent = (
		<>
			<div className="flex items-center justify-between pr-12">
				<h2 className="font-heading text-[36px] font-normal text-royal-blue">
					{personnelName}'s Projects
				</h2>
				<div>
					<SearchBox
						placeholder="Search by role"
						handleFormSubmit={onFilterSubmit}
						keywordFilter
						sectorFilter
						multiSector
						whiteInput
					/>
				</div>
			</div>
			<ModalCloseButton size="lg" />

			<div className="flex flex-col gap-1 pt-12 text-base font-normal text-black">
				<p>
					<span className="font-semibold text-royal-blue">Blue</span>{" "}
					<span className="font-semibold">cards</span> show projects completed
					with the <span className="font-semibold">current employer</span>.
				</p>
				<p>
					<span className="font-semibold text-orange-500">Orange</span>{" "}
					<span className="font-semibold">cards</span> show projects completed
					from a <span className="font-semibold">previous employer</span>.
				</p>
				<p>
					Each card highlights relevant project experience, regardless of when
					it was delivered.
				</p>
			</div>
		</>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			headerContent={headerContent}
		>
			{isLoading ? (
				<div className="flex flex-col items-center justify-center gap-4 space-y-4">
					<Text fontSize="lg">Searching projects...</Text>
					<Spinner size="lg" />
				</div>
			) : error ? (
				<div className="flex flex-col items-center justify-center gap-4 space-y-4">
					<Text fontSize="lg">Error loading projects</Text>
				</div>
			) : displayedProjects.length === 0 ? (
				<div className="flex flex-col items-center justify-center gap-4 space-y-4">
					<Text fontSize="lg">No projects found for this personnel</Text>
				</div>
			) : (
				<div className="grid grid-cols-2 gap-6 lg:grid-cols-3">
					{displayedProjects.map((project, index) => (
						<PersonnelProjectCard
							key={`${project.name}-${index}`}
							project={project}
							index={index}
						/>
					))}
				</div>
			)}
		</Modal>
	);
};
